import axios from 'axios';
import { basicAuthConfig } from '../configs';
import { urlMyspreadApi } from '../urls';

/**
 * API para gestión de Estados y Sub Estados
 * Maneja la comunicación con el backend para tipos de estado, estados y sub-estados
 */

/**
 * Obtener todos los estados organizados por tipo
 * @param {Object} user - Usuario autenticado
 * @returns {Promise} Response con estructura completa de tipos > estados > sub-estados
 */
const getAllEstados = async (user, params = {}) => {
  try {
    const config = basicAuthConfig(user);
    // Evitar doble slash y agregar params si corresponde
    let endpoint = urlMyspreadApi.replace(/\/$/, '') + '/mantenedores/estados/';
    const queryString = new URLSearchParams(params).toString();
    endpoint += `?${queryString}`;
    const response = await axios.get(endpoint, config);
    if (response.data) {
      return {
        success: true,
        data: processEstadosData(response.data),
        message: 'Estados obtenidos exitosamente'
      };
    }
    return {
      success: false,
      error: 'No se recibieron datos del servidor',
      data: []
    };
  } catch (error) {
    console.error('Error al obtener estados:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al obtener estados',
      data: []
    };
  }
};

/**
 * Obtener solo los tipos de estado
 * @param {Object} user - Usuario autenticado
 * @returns {Promise} Response con tipos de estado
 */
const getTiposEstado = async (user) => {
  try {
    const config = basicAuthConfig(user);
    // Evitar doble slash en la URL
    const endpoint = urlMyspreadApi.replace(/\/$/, '') + '/mantenedores/estados/tipos';
    const response = await axios.get(
      endpoint,
      config
    );
    
    if (response.data) {
      return {
        success: true,
        data: response.data,
        message: 'Tipos de estado obtenidos exitosamente'
      };
    }
    
    return {
      success: false,
      error: 'No se recibieron datos del servidor'
    };
  } catch (error) {
    console.error('Error al obtener tipos de estado:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al obtener tipos de estado'
    };
  }
};

/**
 * Crear un nuevo sub-estado
 * @param {Object} user - Usuario autenticado
 * @param {Object} subEstadoData - Datos del sub-estado a crear
 * @returns {Promise} Response con resultado de la operación
 */
/**
 * Crear un nuevo sub-estado
 * @param {Object} user - Usuario autenticado
 * @param {number} estadoId - ID del estado padre
 * @param {Object} subEstadoData - { sub_estado, descripcion }
 * @returns {Promise} Response con resultado de la operación
 */
const createSubEstado = async (user, estadoId, subEstadoData) => {
  try {
    const config = basicAuthConfig(user);
    // Validar datos antes de enviar
    const validationResult = validateSubEstadoData({ ...subEstadoData, estadoId });
    if (!validationResult.isValid) {
      return {
        success: false,
        error: 'Datos inválidos',
        errors: validationResult.errors
      };
    }
    // Solo enviar sub_estado y descripcion en el body
    const body = {
      sub_estado: subEstadoData.sub_estado,
      descripcion: subEstadoData.descripcion
    };
    const response = await axios.post(
      `${urlMyspreadApi}/mantenedores/estados/${estadoId}/subestados`,
      body,
      config
    );
    return {
      success: true,
      data: response.data,
      message: 'Sub-estado creado exitosamente'
    };
  } catch (error) {
    console.error('Error al crear sub-estado:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al crear sub-estado',
      details: error.response?.data
    };
  }
};

/**
 * Actualizar un sub-estado existente
 * @param {Object} user - Usuario autenticado
 * @param {number} subEstadoId - ID del sub-estado a actualizar
 * @param {Object} subEstadoData - Nuevos datos del sub-estado
 * @returns {Promise} Response con resultado de la operación
 */
const updateSubEstado = async (user, subEstadoId, subEstadoData) => {
  try {
    const config = basicAuthConfig(user);
    
    // Validar datos antes de enviar
    const validationResult = validateSubEstadoData(subEstadoData);
    if (!validationResult.isValid) {
      return {
        success: false,
        error: 'Datos inválidos',
        errors: validationResult.errors
      };
    }
    
    const response = await axios.put(
      `${urlMyspreadApi}/mantenedores/estados/subestados/${subEstadoId}/`,
      subEstadoData,
      config
    );
    
    return {
      success: true,
      data: response.data,
      message: 'Sub-estado actualizado exitosamente'
    };
  } catch (error) {
    console.error('Error al actualizar sub-estado:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al actualizar sub-estado',
      details: error.response?.data
    };
  }
};

/**
 * Eliminar un sub-estado
 * @param {Object} user - Usuario autenticado
 * @param {number} subEstadoId - ID del sub-estado a eliminar
 * @returns {Promise} Response con resultado de la operación
 */
const deleteSubEstado = async (user, subEstadoId) => {
  try {
    const config = basicAuthConfig(user);
    const response = await axios.delete(
      `${urlMyspreadApi}/mantenedores/estados/subestados/${subEstadoId}/`,
      config
    );
    
    return {
      success: true,
      message: 'Sub-estado eliminado exitosamente'
    };
  } catch (error) {
    console.error('Error al eliminar sub-estado:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al eliminar sub-estado',
      details: error.response?.data
    };
  }
};

// === FUNCIONES UTILITARIAS ===

/**
 * Procesar datos de estados recibidos del API
 * @param {Object} apiResponse - Respuesta completa del API
 * @returns {Array} Datos procesados con estructura jerárquica
 */
const processEstadosData = (apiResponse) => {
  // La API devuelve datos en formato: { data: [...], message: '', total_estados: n, total_tipos: n }
  if (!apiResponse || !Array.isArray(apiResponse.data)) {
    console.warn('⚠️ processEstadosData: No se recibieron datos válidos del API');
    return [];
  }

  const rawData = apiResponse.data;
  
  // Los datos ya vienen estructurados correctamente desde el API
  // Solo necesitamos mapear para asegurar consistencia
  const processedData = rawData.map(tipo => ({
    id: tipo.id,
    tipo_estado: tipo.tipo_estado,
    total_estados: tipo.total_estados,
    createdBy: tipo.createdBy,
    createdDate: tipo.createdDate,
    estados: tipo.estados.map(estado => ({
      id: estado.id,
      estado: estado.estado,
      check: estado.check,
      createdBy: estado.createdBy,
      createdDate: estado.createdDate,
      modifiedBy: estado.modifiedBy,
      modifiedDate: estado.modifiedDate,
      dispatchTrackId: estado.dispatchTrackId,
      sub_estados: estado.sub_estados.map(subEstado => ({
        id: subEstado.id,
        sub_estado: subEstado.sub_estado,
        descripcion: subEstado.descripcion,
        createdBy: subEstado.createdBy,
        createdDate: subEstado.createdDate,
        dispatchTrackId: subEstado.dispatchTrackId
      }))
    }))
  }));

//   console.log('✅ processEstadosData: Datos procesados correctamente:', processedData);
  return processedData;
};

/**
 * Validar datos de sub-estado
 * @param {Object} data - Datos a validar
 * @returns {Object} Resultado de validación
 */
const validateSubEstadoData = (data) => {
  const errors = {};
  
  if (!data.sub_estado || data.sub_estado.trim().length < 2) {
    errors.sub_estado = 'El nombre del sub-estado debe tener al menos 2 caracteres';
  }
  
  if (!data.descripcion || data.descripcion.trim().length < 5) {
    errors.descripcion = 'La descripción debe tener al menos 5 caracteres';
  }
  
  if (!data.estadoId || isNaN(parseInt(data.estadoId))) {
    errors.estadoId = 'Debe seleccionar un estado padre válido';
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Convertir datos de estados para usar en selects
 * @param {Array} estadosData - Datos procesados de estados
 * @returns {Array} Array formateado para componentes select
 */
const formatEstadosForSelect = (estadosData) => {
  const options = [];
  
  estadosData.forEach(tipo => {
    tipo.estados.forEach(estado => {
      options.push({
        value: estado.id,
        label: `${tipo.tipo} → ${estado.estado}`,
        tipoId: tipo.id,
        tipo: tipo.tipo,
        estado: estado.estado,
        color: estado.color
      });
    });
  });
  
  return options.sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * Buscar en la estructura jerárquica de estados
 * @param {Array} estadosData - Datos de estados
 * @param {string} searchTerm - Término de búsqueda
 * @returns {Array} Resultados filtrados
 */
const filterEstadosData = (estadosData, searchTerm) => {
  if (!searchTerm) return estadosData;
  
  const normalizedSearch = searchTerm.toLowerCase().trim();
  
  return estadosData.map(tipo => {
    const matchedEstados = tipo.estados.filter(estado => {
      // Buscar en estado
      const estadoMatches = 
        estado.estado.toLowerCase().includes(normalizedSearch) ||
        estado.descripcion.toLowerCase().includes(normalizedSearch);
      
      // Buscar en sub-estados
      const subEstadoMatches = estado.sub_estados.some(subEstado =>
        subEstado.sub_estado.toLowerCase().includes(normalizedSearch) ||
        subEstado.descripcion.toLowerCase().includes(normalizedSearch)
      );
      
      return estadoMatches || subEstadoMatches;
    }).map(estado => ({
      ...estado,
      sub_estados: estado.sub_estados.filter(subEstado =>
        subEstado.sub_estado.toLowerCase().includes(normalizedSearch) ||
        subEstado.descripcion.toLowerCase().includes(normalizedSearch)
      )
    }));
    
    // Solo incluir tipos que tengan estados coincidentes
    if (matchedEstados.length > 0 || 
        tipo.tipo.toLowerCase().includes(normalizedSearch) ||
        tipo.descripcion.toLowerCase().includes(normalizedSearch)) {
      return {
        ...tipo,
        estados: matchedEstados
      };
    }
    
    return null;
  }).filter(Boolean);
};

/**
 * Calcular estadísticas de los datos de estados
 * @param {Array} estadosData - Datos de estados
 * @returns {Object} Estadísticas calculadas
 */
const getEstadosStatistics = (estadosData) => {
  const stats = {
    totalTipos: estadosData.length,
    totalEstados: 0,
    totalSubEstados: 0,
    avgSubEstadosPorEstado: 0,
    tipoConMasEstados: null,
    estadoConMasSubEstados: null
  };
  
  let maxEstados = 0;
  let maxSubEstados = 0;
  
  estadosData.forEach(tipo => {
    stats.totalEstados += tipo.estados.length;
    
    if (tipo.estados.length > maxEstados) {
      maxEstados = tipo.estados.length;
      stats.tipoConMasEstados = tipo.tipo;
    }
    
    tipo.estados.forEach(estado => {
      stats.totalSubEstados += estado.sub_estados.length;
      
      if (estado.sub_estados.length > maxSubEstados) {
        maxSubEstados = estado.sub_estados.length;
        stats.estadoConMasSubEstados = estado.estado;
      }
    });
  });
  
  if (stats.totalEstados > 0) {
    stats.avgSubEstadosPorEstado = (stats.totalSubEstados / stats.totalEstados).toFixed(1);
  }
  
  return stats;
};

/**
 * Crear un nuevo estado
 * @param {Object} user - Usuario autenticado
 * @param {Object} estadoData - { Estado, tipo_estado_id, descripcion }
 * @returns {Promise} Response con resultado de la operación
 */
const createEstado = async (user, estadoData) => {
  try {
    const config = basicAuthConfig(user);
    const response = await axios.post(
      `${urlMyspreadApi}/mantenedores/estados/`,
      estadoData,
      config
    );
    return {
      success: true,
      data: response.data,
      message: 'Estado creado exitosamente'
    };
  } catch (error) {
    console.error('Error al crear estado:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al crear estado',
      details: error.response?.data
    };
  }
};

// =============================================================================
// EXPORTS
// =============================================================================

export {
  getAllEstados,
  getTiposEstado,
  createEstado,
  createSubEstado,
  updateSubEstado,
  deleteSubEstado,
  formatEstadosForSelect,
  filterEstadosData,
  getEstadosStatistics,
  validateSubEstadoData
};
